import React, { useCallback } from 'react';
import { PermissionType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAChipProps, DNAChipStatusEnum, DNAConditionalWrapper, DNAText } from '@alucio/lux-ui';
import { DNAChip, DNAConditionalButton, DNAPopover, useCurrentPage, useDNACustomDeckActions } from 'src/components/DNA';
import {
  CustomDeckORM,
  FolderItemORM,
  FolderORM,
  isCustomDeckORM,
  isFolderItemORM,
  isFolderORM,
  ORMTypes,
} from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { getRootFolderORM } from 'src/state/redux/selector/folder';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { isSharedEditableFolder } from 'src/utils/foldersHelpers';

interface CustomDeckReviewButtonProps {
  customDeckORM: CustomDeckORM;
  folderItemORM: FolderItemORM;
  onRowPress?: (isThumbnail?: boolean) => void;
}
interface DNAFolderStatusChipProps extends Omit<DNAChipProps, 'onPress'> {
  orm?: FolderORM | FolderItemORM;
  variant?: 'List' | 'Card';
  folderStatus?: FOLDER_BADGE_STATUS;
  popoverText?: string;
  onRowPress?: (isThumbnail?: boolean) => void;
}

const FOLDER_STATUS_DISPLAY_TEXT = {
  SHARED: 'SHARED',
  SHARED_WITH_ME: 'SHARED WITH ME',
  NEEDS_REVIEW: 'NEEDS REVIEW',
  UPDATED: 'UPDATED',
}

export type FOLDER_BADGE_STATUS = keyof typeof FOLDER_STATUS_DISPLAY_TEXT;

const FOLDER_STATUS_DISPLAY_STATUS: Record<FOLDER_BADGE_STATUS, DNAChipStatusEnum> = {
  SHARED: DNAChipStatusEnum.secondary,
  SHARED_WITH_ME: DNAChipStatusEnum.secondary,
  NEEDS_REVIEW: DNAChipStatusEnum.warning,
  UPDATED: DNAChipStatusEnum.success,
};

export const FOLDER_STATUS_POPOVER_TEXT = {
  [ORMTypes.FOLDER]: {
    SHARED: 'You shared this folder',
    SHARED_WITH_ME: 'This folder was shared with you',
    NEEDS_REVIEW: 'File(s) in this folder need to be reviewed',
    UPDATED: 'File(s) in this folder have been auto-updated to a new version',
  },
  [ORMTypes.FOLDER_ITEM]: {
    SHARED: 'You shared this file',
    SHARED_WITH_ME: 'This file was shared with you',
    NEEDS_REVIEW: 'File may have outdated content from an older version',
    UPDATED: 'This file was auto-updated to a new version',
  },
};

const CustomDeckReviewButton: React.FC<CustomDeckReviewButtonProps> = (props) => {
  const { customDeckORM, folderItemORM, onRowPress } = props;
  const { isOnline } = useAppSettings();
  const route = useCurrentPage({ exact: false });
  const DNACustomDeckActions = useDNACustomDeckActions();
  const enableCustomDecksCollaborationEdit = useFeatureFlags('enableCustomDecksCollaborationEdit');
  const openCustomDeckEditor = DNACustomDeckActions.edit(customDeckORM, folderItemORM, 'REVIEW');
  const { containsWebDocs, assets:{ isContentCached } } = customDeckORM.meta;
  const contentAvailable = isOnline || (isContentCached && !containsWebDocs);
  const isCustomDeckNeedsReview = customDeckORM.meta.version.requiresReview;
  const rootFolder = folderItemORM.relations.parentORM
    ? getRootFolderORM(folderItemORM.relations.parentORM)
    : folderItemORM.relations.parentORM;
  const hasEditPermission = rootFolder?.meta.permission === PermissionType.EDIT;
  const isSharedFolders = route?.PATH.includes('shared_folders');
  const isSharedEditFolder = (rootFolder && isSharedEditableFolder(rootFolder)) || false;
  const isReviewAllowed = hasEditPermission &&
    ((!isSharedFolders || isSharedEditFolder) || enableCustomDecksCollaborationEdit)

  const handleReviewButtonPress = useCallback(() => {
    if (isCustomDeckNeedsReview && isReviewAllowed) openCustomDeckEditor()
    else onRowPress?.()
  }, [isCustomDeckNeedsReview, isReviewAllowed, openCustomDeckEditor, onRowPress, folderItemORM])

  if (!isCustomDeckNeedsReview) return null;
  return (
    <DNAConditionalButton
      testID="customDeck-review-row-button"
      status="warning"
      size="sm"
      onPress={() => handleReviewButtonPress()}
    >
      <DNAConditionalButton.Disable condition={!contentAvailable}>
        <DNAText colorMode="dark">This file must be synced prior to review.</DNAText>
      </DNAConditionalButton.Disable>
      Review slides
    </DNAConditionalButton>
  )
}

/**
 * Beacon-specific DNAChip component for folder & folder item status
 */
export const DNAFolderStatusChip: React.FC<DNAFolderStatusChipProps> = (props) => {
  const { orm, variant = 'Card', folderStatus, popoverText, onRowPress, ...rest } = props;
  const route = useCurrentPage({ exact: false });

  if (orm) {
    const isSharedFolders = route?.PATH.includes('shared_folders');
    const isArchivedFolders = route?.PATH.includes('archived_folders');
    const displayChipStatuses: FOLDER_BADGE_STATUS[] = [];
    const isSharedWithMe = isFolderORM(orm) && orm.meta.isSharedWithTheUser;
    const isSharedByMe = isFolderORM(orm) && orm.meta.isSharedFolder && !isSharedWithMe;

    const displaySharedWithMeBadge = !isSharedFolders && !isArchivedFolders && isSharedWithMe;
    const displaySharedBadge = !isSharedFolders && !isArchivedFolders && isSharedByMe;

    if (displaySharedBadge && variant === 'Card') displayChipStatuses.push('SHARED');
    if (displaySharedWithMeBadge && variant === 'Card') displayChipStatuses.push('SHARED_WITH_ME');
    const hasAutoUpdate = isFolderORM(orm)
      ? orm.meta.version.containsAutoUpdatedItem
      : orm.meta.hasAutoUpdatedItem;
    if (hasAutoUpdate) displayChipStatuses.push('UPDATED');
    const containsPendingReview = isFolderORM(orm) && orm.meta.version.containsPendingReviewItem;
    const hasOutdated = isFolderORM(orm)
      ? orm.meta.version.containsOutdatedDocVer
      : isFolderItemORM(orm) && isCustomDeckORM(orm.relations.itemORM)
        ? orm.meta.hasOutdatedItem
        : false;
    if (containsPendingReview || hasOutdated) displayChipStatuses.push('NEEDS_REVIEW');
    const applyBadgeGap = variant === 'Card' && isFolderORM(orm);

    return (
      <DNABox fill alignX="center" alignY="center" spacing="sm" wrap="center">
        {displayChipStatuses.map(status => {
          if (
            status === 'NEEDS_REVIEW' &&
            isFolderItemORM(orm) &&
            isCustomDeckORM(orm.relations.itemORM)
          ) {
            return (
              <CustomDeckReviewButton
                key={status}
                customDeckORM={orm.relations.itemORM}
                folderItemORM={orm}
                onRowPress={onRowPress}
              />
            )
          } else {
            const defaultPopoverText = FOLDER_STATUS_POPOVER_TEXT[orm.type][status];
            return (
              <DNAConditionalWrapper
                key={status}
                condition={!!defaultPopoverText}
                wrapper={children => (
                  <DNAPopover placement="top" disablePopover={['tabletPWA']}>
                    <DNAPopover.Anchor>
                      {children}
                    </DNAPopover.Anchor>
                    <DNAPopover.Content offset={2}>
                      <DNAText colorMode="dark">
                        {defaultPopoverText}
                      </DNAText>
                    </DNAPopover.Content>
                  </DNAPopover>
                )}
              >
                <DNAChip.Beacon
                  key={status}
                  testID="folder-status-badge"
                  status={FOLDER_STATUS_DISPLAY_STATUS[status]}
                  children={FOLDER_STATUS_DISPLAY_TEXT[status]}
                  style={applyBadgeGap ? { marginTop: 8 } : undefined}
                  {...rest}
                />
              </DNAConditionalWrapper>
            )
          }
        })}
      </DNABox>
    )
  }
  else if (folderStatus) {
    return (
      <DNAConditionalWrapper
        key={folderStatus}
        condition={!!popoverText}
        wrapper={children => (
          <DNAPopover placement="top" disablePopover={['tabletPWA']}>
            <DNAPopover.Anchor>
              {children}
            </DNAPopover.Anchor>
            <DNAPopover.Content offset={2}>
              <DNAText colorMode="dark">
                {popoverText}
              </DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        )}
      >
        <DNAChip.Beacon
          testID="folder-status-badge"
          status={FOLDER_STATUS_DISPLAY_STATUS[folderStatus]}
          children={FOLDER_STATUS_DISPLAY_TEXT[folderStatus]}
          {...rest}
        />
      </DNAConditionalWrapper>
    );
  }
  else return null;
};

export default DNAFolderStatusChip;
